import * as React from 'react';
import { ReactNode } from 'react';

import { PictureProfile } from '../../../ResizedImage/PictureProfile.csharp';
import { Loader } from '../../Components/Loader/Loader';
import { ComponentProps } from '../ComponentProps.csharp';
import { ResponsivePictureBuilder } from './ResponsivePictureBuilder';
import { ResponsivePictureViewModel } from './ResponsivePictureViewModel.csharp';

export interface ResponsivePictureProps extends ComponentProps<ResponsivePictureViewModel> {
    profile: PictureProfile;
    imgAttributes?: React.ImgHTMLAttributes<HTMLImageElement>;
    showLoader?: boolean;
    isLazyLoaded?: boolean;
}

export class ResponsivePicture extends React.Component<ResponsivePictureProps, { isLoading: boolean }> {
    responsivePictureBuilder: ResponsivePictureBuilder;

    constructor(props: ResponsivePictureProps) {
        super(props);
        this.state = {
            isLoading: true
        };
        this.responsivePictureBuilder = new ResponsivePictureBuilder(props.profile, props.model);
    }

    private imgRef = React.createRef<HTMLImageElement>();

    public render(): ReactNode {
        const { isLazyLoaded } = this.props;
        return this.props.model ? (
            <picture>
                {isLazyLoaded ? this.buildSourcesWithLazyLoading() : this.buildSources()}
                {isLazyLoaded ? this.renderImageWithLazyLoading() : this.renderImage()}
                {isLazyLoaded && <Loader isLoading={this.state.isLoading} />}
            </picture>
        ) : null;
    }

    public componentDidMount() {
        const isImageLoadingCompleted = this.imgRef.current && this.imgRef.current.complete && this.imgRef.current.naturalHeight !== 0;

        this.setState({
            isLoading: isImageLoadingCompleted ? false : this.props.showLoader === true
        });
        // eslint-disable-next-line
        // @ts-ignore
        if (window.objectFitPolyfill) {
            // eslint-disable-next-line
            // @ts-ignore
            window.objectFitPolyfill();
        }
    }

    private renderImage = () => (
        <img
            ref={this.imgRef}
            alt={this.props.model.alt || ''}
            src={this.responsivePictureBuilder.getDefaultUrl()}
            data-object-fit="cover"
            data-object-position="center"
            {...this.props.imgAttributes}
        />
    );

    private renderImageWithLazyLoading = () => (
        <img
            className="lazyload"
            ref={this.imgRef}
            alt={this.props.model.alt || ''}
            data-src={this.responsivePictureBuilder.getDefaultUrl()}
            data-sizes="auto"
            data-object-fit="cover"
            data-object-position="center"
            {...this.props.imgAttributes}
            onLoad={this.handleOnLoad}
            onError={this.handleOnLoad}
        />
    );

    private buildSources = (): ReactNode[] => {
        if (!this.props.profile.sources) return [];
        return this.props.profile.sources.map((source) => (
            <source
                key={source.mediaCondition}
                media={source.mediaCondition}
                srcSet={this.responsivePictureBuilder.getSrcSet(source)}
                sizes={source.sizes.join(', ')}
            />
        ));
    };

    private buildSourcesWithLazyLoading = (): ReactNode[] => {
        if (!this.props.profile.sources) return [];
        return this.props.profile.sources.map((source) => (
            <source key={source.mediaCondition} media={source.mediaCondition} data-srcset={this.responsivePictureBuilder.getSrcSet(source)} data-sizes="auto" />
        ));
    };

    private handleOnLoad = () => {
        this.setState({ isLoading: false });
    };
}
